import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ArrowRight, RefreshCw, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const BAND_COLORS = {
    "Excellent": { bg: "#E7F7EE", fg: "#16A34A" },
    "Great": { bg: "#DBE7FD", fg: "#3A80F7" },
    "Good": { bg: "#FEF3C7", fg: "#B45309" },
    "Needs work": { bg: "#FEE2E2", fg: "#DC2626" },
    "Poor": { bg: "#FEE2E2", fg: "#DC2626" },
};

/**
 * Digital Identity Score dashboard widget.
 *
 * Shows: score, band label, per-dimension breakdown, top-3 rule-derived
 * suggestions. Recomputes on demand.
 */
export default function IdentityScoreWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/me/identity-score");
            setData(r.data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    if (!data) {
        return (
            <div className="ddi-stat-card" data-testid="identity-score-widget-loading">
                <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">
                    Digital Identity Score
                </div>
                <div className="text-[#94A3B8]">Computing…</div>
            </div>
        );
    }

    const bandStyle = BAND_COLORS[data.label] || BAND_COLORS["Good"];

    return (
        <div className="ddi-card p-5 lg:col-span-2" data-testid="identity-score-widget">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={14} className="text-[#3A80F7]" />
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                            Digital Identity Score
                        </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                        <div className="text-[42px] font-bold text-[#0F172A] leading-none tracking-tight" data-testid="identity-score-value">
                            {data.score}
                            <span className="text-[#94A3B8] text-[20px] font-normal">/100</span>
                        </div>
                        <span
                            className="ddi-badge"
                            style={{ background: bandStyle.bg, color: bandStyle.fg }}
                            data-testid="identity-score-label"
                        >
                            {data.label}
                        </span>
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="text-[#64748B] hover:text-[#3A80F7] transition-colors p-1.5 rounded-md hover:bg-[#EEF4FE]"
                    data-testid="identity-score-refresh"
                    title="Recompute"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            {/* Per-dimension breakdown bars */}
            <div className="space-y-2.5 mb-4" data-testid="identity-score-breakdown">
                {(data.breakdown || []).map((row) => {
                    const pctOfMax = row.max_points > 0 ? Math.round((row.points / row.max_points) * 100) : 0;
                    return (
                        <div key={row.key} data-testid={`identity-dim-${row.key}`}>
                            <div className="flex items-center justify-between text-[12px] mb-1">
                                <span className="text-[#334155] font-medium">{row.label}</span>
                                <span className="text-[#64748B]">
                                    {row.points}<span className="text-[#94A3B8]"> / {row.max_points}</span>
                                </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                                <div
                                    className={"h-full transition-all " + (pctOfMax === 100 ? "bg-[#16A34A]" : pctOfMax >= 60 ? "bg-[#3A80F7]" : pctOfMax >= 30 ? "bg-[#F59E0B]" : "bg-[#DC2626]")}
                                    style={{ width: `${pctOfMax}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Top 3 improvement suggestions */}
            {data.suggestions && data.suggestions.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[#E5E9F2]" data-testid="identity-score-suggestions">
                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2.5">
                        Top improvements
                    </div>
                    <ul className="space-y-2">
                        {data.suggestions.map((s) => (
                            <li key={s.key} className="flex items-start gap-3 group" data-testid={`identity-suggestion-${s.key}`}>
                                <div className="mt-1 h-6 w-6 rounded-lg bg-[#EEF4FE] text-[#3A80F7] flex items-center justify-center text-[11px] font-bold shrink-0">
                                    +{s.potential_points}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-semibold text-[#0F172A]">{s.title}</div>
                                    <div className="text-[11.5px] text-[#64748B] leading-snug mt-0.5">{s.description}</div>
                                </div>
                                <Link
                                    to={s.fix_link}
                                    className="text-[11px] text-[#3A80F7] font-semibold hover:underline whitespace-nowrap self-center inline-flex items-center gap-0.5"
                                    data-testid={`identity-suggestion-cta-${s.key}`}
                                >
                                    Fix <ArrowRight size={11} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
